import React, { Component } from "react";
import PropTypes from "prop-types";
import { List, OrderedMap } from "immutable";
import {
    Editor,
    EditorState,
    CharacterMetadata,
    ContentState,
    ContentBlock
} from "draft-js";
import { diffChars } from "diff";
import {
    addedStyle,
    contentStateToCorrection,
    correctionToContentState,
    removedStyle
} from "./postConversion";
import { v4 } from "uuid";

const customStyleMap = {
    [addedStyle]: {
        color: "#388E3C"
    },
    [removedStyle]: {
        textDecoration: "line-through",
        color: "rgb(255,182,193)"
    }
};

function diffZipBlock(diff, oldChars, newChars) {
    if (diff.isEmpty()) {
        return List();
    }
    const changeObj = diff.get(0);
    let restOldChars = oldChars;
    let restNewChars = newChars;
    let changedChars;
    if (changeObj.added) {
        changedChars = newChars.take(changeObj.count);
        changedChars = changedChars.map(([metadata, char]) => {
            let newMetadata = CharacterMetadata.removeStyle(
                metadata,
                removedStyle
            );
            newMetadata = CharacterMetadata.applyStyle(newMetadata, addedStyle);
            return [newMetadata, char];
        });
        restNewChars = newChars.skip(changeObj.count);
    } else if (changeObj.removed) {
        changedChars = oldChars.take(changeObj.count);
        changedChars = changedChars
            .filter(([metadata]) => {
                return !metadata.hasStyle(addedStyle);
            })
            .map(([metadata, char]) => {
                let newMetadata = CharacterMetadata.removeStyle(
                    metadata,
                    addedStyle
                );
                newMetadata = CharacterMetadata.applyStyle(
                    newMetadata,
                    removedStyle
                );
                return [newMetadata, char];
            });
        restOldChars = oldChars.skip(changeObj.count);
    } else {
        changedChars = oldChars.take(changeObj.count);
        restOldChars = oldChars.skip(changeObj.count);
        restNewChars = newChars.skip(changeObj.count);
    }
    return changedChars.concat(
        diffZipBlock(diff.rest(), restOldChars, restNewChars)
    );
}

function merge(oldBlocks, newBlocks) {
    // Find all the old blocks that aren't in new blocks
    // insert old blocks into the correct position in new blocks
    let toReturn = List(newBlocks.toArray());
    let blocksToInsert = [];
    oldBlocks.forEach((oldBlock, key) => {
        const newBlock = newBlocks.get(key);
        if (!newBlock && oldBlock.getText() !== "") {
            blocksToInsert.push(oldBlock);
        } else if (blocksToInsert.length > 0) {
            let index = toReturn.findIndex(block => block.getKey() === key);
            toReturn = toReturn.splice(index, 0, ...blocksToInsert);
            blocksToInsert = [];
        }
    });
    if (blocksToInsert.length > 0) {
        toReturn = toReturn.push(...blocksToInsert);
    }
    return OrderedMap(toReturn.map(value => [value.getKey(), value]));
}

function rearrangeDiffBySelectionState(diff, newBlock, selectionState) {
    if (newBlock.getKey() === selectionState.getAnchorKey()) {
        const anchorOffset = selectionState.getAnchorOffset();
        return diff.reduce(
            (
                { diffSoFar, index, skip },
                { removed, added, value, count },
                idx
            ) => {
                if (skip) {
                    return { diffSoFar, index };
                }

                const inRange =
                    anchorOffset >= index && anchorOffset <= index + count;
                if (inRange && diff.count() > idx + 1) {
                    const nextChangeObject = diff.get(idx + 1);
                    if (nextChangeObject.removed || nextChangeObject.added) {
                        let suffixValue = value,
                            limit = index + count;

                        const offsetLimit =
                            anchorOffset -
                            (nextChangeObject.added
                                ? nextChangeObject.count
                                : 0);
                        while (
                            suffixValue.endsWith(nextChangeObject.value) &&
                            offsetLimit < limit
                        ) {
                            limit -= nextChangeObject.count;
                            suffixValue = suffixValue.substring(
                                0,
                                suffixValue.length - nextChangeObject.count
                            );
                        }
                        if (limit === offsetLimit || limit === anchorOffset) {
                            const offset = index + count - limit;
                            const length = value.length - offset;
                            let afterValue = value.substring(length);
                            return {
                                skip: true,
                                diffSoFar: diffSoFar.push(
                                    {
                                        removed,
                                        added,
                                        value: value.substring(0, length),
                                        count: length
                                    },
                                    nextChangeObject,
                                    {
                                        value: afterValue,
                                        count: afterValue.length
                                    }
                                ),
                                index: index + count + nextChangeObject.count
                            };
                        }
                    }
                }

                return {
                    diffSoFar: diffSoFar.push({
                        removed,
                        added,
                        value,
                        count
                    }),
                    index: index + count
                };
            },
            {
                diffSoFar: List(),
                index: 0
            }
        ).diffSoFar;
    }
    return diff;
}

function reverse(s) {
    return s.split("").reverse().join("");
}

function smallestDiffChars(oldText, newText) {
    let diff = List(diffChars(oldText, newText));
    let reverseDiff = List(diffChars(reverse(oldText), reverse(newText)));
    if (diff.count() <= reverseDiff.count()) {
        return diff;
    }
    return reverseDiff.reverse().map(changeObj => ({
        ...changeObj,
        value: reverse(changeObj.value)
    }));
}

function getRelativeRange(
    startKey,
    startOffset,
    endKey,
    endOffset,
    editorState,
    block
) {
    const blockMap = editorState.getCurrentContent().getBlockMap();

    const currentKey = block.getKey();
    let i = 0;
    let startKeyIndex, endKeyIndex, currentKeyIndex;
    blockMap.forEach((_, key) => {
        if (startKey === key) {
            startKeyIndex = i;
        }
        if (endKey === key) {
            endKeyIndex = i;
        }
        if (currentKey === key) {
            currentKeyIndex = i;
        }
        i += 1;
    });

    const keys = [
        {
            key: startKey,
            offset: startOffset,
            index: startKeyIndex
        },
        {
            key: endKey,
            offset: endOffset,
            index: endKeyIndex
        }
    ];

    keys.sort((a, b) => {
        let indexDiff = a.index - b.index;
        return indexDiff === 0 ? a.offset - b.offset : indexDiff;
    });

    startKeyIndex = keys[0].index;
    startKey = keys[0].key;
    startOffset = keys[0].offset;

    endKeyIndex = keys[1].index;
    endKey = keys[1].key;
    endOffset = keys[1].offset;

    let beginSelectionOffset = startOffset;
    let endSelectionOffset = endOffset;

    if (
        currentKeyIndex === undefined ||
        startKeyIndex === undefined ||
        endKeyIndex === undefined ||
        startKeyIndex > currentKeyIndex ||
        endKeyIndex < currentKeyIndex
    ) {
        return { invalid: true };
    }

    if (startKeyIndex < currentKeyIndex) {
        beginSelectionOffset = 0;
    }
    if (endKeyIndex > currentKeyIndex) {
        endSelectionOffset = block.getText().length;
    }
    return { begin: beginSelectionOffset, end: endSelectionOffset };
}

function annotateCount(diff) {
    diff.forEach(changeObj => {
        changeObj.count = changeObj.value.length;
    });
    return diff;
}

function determineDiff(range, oldText, newText) {
    if (!range.invalid) {
        const { begin, end } = range;
        const oldTextBefore = oldText.slice(0, begin);
        const oldTextAfter = oldText.slice(end, oldText.length);
        const removeRangeText = oldTextBefore + oldTextAfter;
        if (removeRangeText === newText) {
            return annotateCount([
                { value: oldTextBefore },
                { removed: true, value: oldText.slice(begin, end) },
                { value: oldTextAfter }
            ]);
        }
        let countDiff = newText.length - removeRangeText.length;
        if (countDiff > 0) {
            const addedStr = newText.slice(begin, begin + countDiff);
            if (oldTextBefore + addedStr + oldTextAfter === newText) {
                return annotateCount([
                    { value: oldTextBefore },
                    { added: true, value: addedStr },
                    { removed: true, value: oldText.slice(begin, end) },
                    { value: oldTextAfter }
                ]);
            }
        }
    }
}

function determineDiffBySelection(
    newSelectionState,
    oldSelectionState,
    newBlock,
    oldBlock,
    oldEditorState
) {
    const startKey = oldSelectionState.getStartKey();
    const startOffset = oldSelectionState.getStartOffset();

    const endKey = oldSelectionState.getEndKey();
    const endOffset = oldSelectionState.getEndOffset();

    const oldAnchorKey = oldSelectionState.getAnchorKey();
    const oldAnchorOffset = oldSelectionState.getAnchorOffset();

    const newAnchorKey = newSelectionState.getAnchorKey();
    const newAnchorOffset = newSelectionState.getAnchorOffset();

    const oldText = oldBlock.getText();
    const newText = newBlock.getText();

    // Find the text in the old range
    // If it's remove range, remove the text and see if matches the old text
    let relativeRange = getRelativeRange(
        startKey,
        startOffset,
        endKey,
        endOffset,
        oldEditorState,
        oldBlock
    );

    let diff = determineDiff(relativeRange, oldText, newText);

    // If not, try it with the new anchor key and the old anchor key
    // If it's the same text, then remove the text
    if (!diff) {
        relativeRange = getRelativeRange(
            oldAnchorKey,
            oldAnchorOffset,
            newAnchorKey,
            newAnchorOffset,
            oldEditorState,
            oldBlock
        );
        diff = determineDiff(relativeRange, oldText, newText);
    }
    return diff;
}

function zipBlockContents(block) {
    return block.getCharacterList().zip(List(block.getText().split("")));
}

function unzipContents(zippedChars) {
    return zippedChars.reduce(
        ([characterList, text], [metadata, char]) => {
            return [characterList.push(metadata), text + char];
        },
        [List(), ""]
    );
}

function isRemoveBlock(oldEditorState, editorState) {
    if (editorState.getLastChangeType() === "backspace-character") {
        const oldBlocks = oldEditorState.getCurrentContent().getBlockMap();
        const newBlocks = editorState.getCurrentContent().getBlockMap();
        return oldBlocks.count() - 1 === newBlocks.count();
    }
}

class CorrectionTextEdit extends Component {
    constructor(props) {
        super(props);
        const contentState = CorrectionTextEdit.deriveContentStateFromProps(
            props
        );
        this.state = {
            undoStack: List(),
            redoStack: List(),
            editorState: EditorState.createWithContent(contentState)
        };
    }

    static deriveContentStateFromProps(props) {
        const { body, edits } = props;

        const blocks = correctionToContentState({
            body,
            edits
        });
        return ContentState.createFromBlockArray(blocks.toArray());
    }

    getCorrection() {
        return contentStateToCorrection(
            this.state.editorState.getCurrentContent()
        );
    }

    componentWillReceiveProps(nextProps) {
        // If we're using this as an editor, it's too computationally expensive and
        // we lose selection info with the editor state if we convert every
        // time we receive new props.
        // This is a limitation of the current implementation, e.g. the props
        // currently are only initialized on construction when readOnly is false.
        if (nextProps.readOnly) {
            let contentState = CorrectionTextEdit.deriveContentStateFromProps(
                nextProps
            );

            this.setState({
                undoStack: List(),
                redoStack: List(),
                editorState: EditorState.createWithContent(contentState)
            });
        }
    }

    revertSelection() {
        const editorState = this.state.editorState;
        const selectionState = editorState.getSelection();
        const startKey = selectionState.getStartKey();
        const startOffset = selectionState.getStartOffset();

        const endKey = selectionState.getEndKey();
        const endOffset = selectionState.getEndOffset();

        if (startKey === endKey && startOffset === endOffset) {
            return;
        }

        const contentState = editorState.getCurrentContent();

        const newBlocks = contentState.getBlockMap().map((block, key) => {
            const range = getRelativeRange(
                startKey,
                startOffset,
                endKey,
                endOffset,
                editorState,
                block
            );
            if (range.invalid) {
                return block;
            }
            const { begin, end } = range;
            const zippedChars = zipBlockContents(block);
            const revertedChars = zippedChars
                .map(([metadata, char], i) => {
                    if (i >= begin || i <= end) {
                        if (metadata.hasStyle(removedStyle)) {
                            return [
                                CharacterMetadata.removeStyle(
                                    metadata,
                                    removedStyle
                                ),
                                char
                            ];
                        } else if (metadata.hasStyle(addedStyle)) {
                            return null;
                        }
                    }
                    return [metadata, char];
                })
                .filter(v => !!v);

            const [newCharacterList, text] = unzipContents(revertedChars);
            return block.merge({
                characterList: newCharacterList,
                text
            });
        });

        this.updateContentState(newBlocks, editorState);
    }

    focus = () => {
        if (this.component) {
            this.component.focus();
        }
    };

    updateContentState(newBlocks, editorState) {
        const { undoStack } = this.state;
        const newContentState = ContentState.createFromBlockArray(
            newBlocks.toArray()
        );
        const newEditorState = EditorState.set(editorState, {
            currentContent: newContentState
        });
        this.setState({
            undoStack: undoStack.push(this.state.editorState),
            redoStack: List(),
            editorState: newEditorState
        });
    }

    undo = () => {
        const { undoStack, redoStack, editorState } = this.state;
        if (undoStack.count() > 0) {
            this.setState({
                undoStack: undoStack.pop(),
                editorState: undoStack.last(),
                redoStack: redoStack.push(editorState)
            });
        }
    };

    redo = () => {
        const { undoStack, redoStack, editorState } = this.state;
        if (redoStack.count() > 0) {
            this.setState({
                undoStack: undoStack.push(editorState),
                editorState: redoStack.last(),
                redoStack: redoStack.pop()
            });
        }
    };

    onChange = editorState => {
        const oldEditorState = this.state.editorState;

        const newSelectionState = editorState.getSelection();
        const oldSelectionState = oldEditorState.getSelection();
        const changeType = editorState.getLastChangeType();

        if (changeType === "undo") {
            this.undo();
        } else if (changeType === "redo") {
            this.redo();
        } else if (
            changeType === "split-block" ||
            isRemoveBlock(oldEditorState, editorState)
        ) {
            this.setState({ editorState });
        } else if (
            oldEditorState.getCurrentContent() !==
            editorState.getCurrentContent()
        ) {
            const content = editorState.getCurrentContent();
            const blocks = content.getBlockMap();
            const oldBlocks = oldEditorState.getCurrentContent().getBlockMap();

            const mergedBlocks = merge(oldBlocks, blocks);
            const newBlocks = mergedBlocks.map((block, key) => {
                let newBlock =
                    blocks.get(key) ||
                    new ContentBlock({ key: v4().replace(/-/g, "") });

                let oldBlock =
                    oldBlocks.get(key) ||
                    new ContentBlock({ key: v4().replace(/-/g, "") });

                if (newBlock === oldBlock) {
                    return newBlock;
                }

                const oldText = oldBlock.getText();
                const newText = newBlock.getText();
                let selectionDiff = determineDiffBySelection(
                    newSelectionState,
                    oldSelectionState,
                    newBlock,
                    oldBlock,
                    oldEditorState
                );

                let diff;
                if (selectionDiff) {
                    diff = List(selectionDiff);
                } else {
                    diff = List(smallestDiffChars(oldText, newText));
                    diff = rearrangeDiffBySelectionState(
                        diff,
                        newBlock,
                        newSelectionState
                    );
                }

                const oldChars = zipBlockContents(oldBlock);
                const newChars = zipBlockContents(newBlock);

                let newZippedChars = diffZipBlock(
                    diff,
                    oldChars,
                    newChars,
                    newSelectionState
                );
                const [newCharacterList, text] = unzipContents(newZippedChars);
                return newBlock.merge({
                    characterList: newCharacterList,
                    text
                });
            });

            this.updateContentState(newBlocks, editorState);
        } else {
            this.setState({ editorState });
        }
        this.props.onChange(editorState);
    };

    render() {
        const { readOnly } = this.props;
        return (
            <Editor
                ref={component => {
                    this.component = component;
                }}
                allowUndo={false}
                readOnly={readOnly}
                customStyleMap={customStyleMap}
                editorState={this.state.editorState}
                onChange={this.onChange}
            />
        );
    }
}

CorrectionTextEdit.propTypes = {
    body: PropTypes.string,
    readOnly: PropTypes.bool,
    edits: PropTypes.object,
    onChange: PropTypes.func
};

CorrectionTextEdit.defaultProps = {
    readOnly: false,
    onChange: () => {}
};

export default CorrectionTextEdit;



// WEBPACK FOOTER //
// ./src/components/CorrectionTextEdit.jsx